import React, { useState, useEffect, useCallback } from 'react';
import { X, Users, Mail, Phone, User, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { educationAPI } from '../api/axiosClient';
import { GetStudentDetailsResponse, ParentDto, StudentStatus, Gender } from '../api/types';
import toast from 'react-hot-toast';

interface StudentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
}

const StudentDetailModal: React.FC<StudentDetailModalProps> = ({
  isOpen,
  onClose,
  studentId
}) => {
  const [student, setStudent] = useState<GetStudentDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadStudentDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await educationAPI.getStudentDetails(studentId);
      if (response.success && response.data) {
        setStudent(response.data);
      }
    } catch (error: any) {
      console.error('Error loading student details:', error);
      toast.error('Öğrenci detayları yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (isOpen && studentId) {
      loadStudentDetails();
    }
  }, [isOpen, studentId, loadStudentDetails]);

  const getGenderName = (gender: Gender): string => {
    switch (gender) {
      case Gender.Male:
        return 'Erkek';
      case Gender.Female:
        return 'Kadın';
      case Gender.Other:
        return 'Diğer';
      default:
        return 'Bilinmiyor';
    }
  };

  const getStatusName = (status: StudentStatus): string => {
    switch (status) {
      case StudentStatus.Active:
        return 'Aktif';
      case StudentStatus.Graduated:
        return 'Mezun';
      case StudentStatus.Transferred:
        return 'Nakil';
      case StudentStatus.Suspended:
        return 'Askıda';
      case StudentStatus.Left:
        return 'Ayrıldı';
      case StudentStatus.Deleted:
        return 'Silindi';
      default:
        return 'Bilinmiyor';
    }
  };

  const getStatusColor = (status: StudentStatus): string => {
    switch (status) {
      case StudentStatus.Active:
        return 'bg-green-100 text-green-800';
      case StudentStatus.Graduated:
        return 'bg-blue-100 text-blue-800';
      case StudentStatus.Transferred:
        return 'bg-yellow-100 text-yellow-800';
      case StudentStatus.Suspended:
        return 'bg-orange-100 text-orange-800';
      case StudentStatus.Left:
        return 'bg-red-100 text-red-800';
      case StudentStatus.Deleted:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRelationTypeName = (relationType: number): string => {
    switch (relationType) {
      case 1:
        return 'Anne';
      case 2:
        return 'Baba';
      case 3:
        return 'Vasi';
      case 4:
        return 'Diğer';
      default:
        return 'Bilinmiyor';
    }
  };

  const getRelationTypeColor = (relationType: number): string => {
    switch (relationType) {
      case 1:
        return 'bg-pink-100 text-pink-800';
      case 2:
        return 'bg-blue-100 text-blue-800';
      case 3:
        return 'bg-green-100 text-green-800';
      case 4:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <GraduationCap className="w-6 h-6 mr-2 text-[#9B51E0]" />
              Öğrenci Detayları
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0]"></div>
            </div>
          ) : student ? (
            <div className="space-y-6">
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Kişisel Bilgiler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Ad Soyad</label>
                        <p className="text-lg font-semibold text-gray-900">
                          {student.firstName} {student.lastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Öğrenci Numarası</label>
                        <p className="text-gray-900 font-mono">{student.studentNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Kimlik Numarası</label>
                        <p className="text-gray-900 font-mono">{student.identityNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Sınıf</label>
                        <p className="text-gray-900">
                          {student.classroomName || '-'}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Cinsiyet</label>
                        <p className="text-gray-900">{getGenderName(student.gender)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Doğum Tarihi</label>
                        <p className="text-gray-900">
                          {student.birthDate ? new Date(student.birthDate).toLocaleDateString('tr-TR') : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Durum</label>
                        <div className="mt-1">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              student.status
                            )}`}
                          >
                            {getStatusName(student.status)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Kayıt Tarihi</label>
                        <p className="text-gray-900">
                          {student.enrollmentDate ? new Date(student.enrollmentDate).toLocaleDateString('tr-TR') : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Parents Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Users className="w-5 h-5 mr-2 text-orange-600" />
                    Veliler ({student.parents?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {student.parents && student.parents.length > 0 ? (
                    <div className="space-y-3">
                      {student.parents.map((parent: ParentDto) => (
                        <div
                          key={parent.parentId}
                          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-[#9B51E0] rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">
                                  {parent.fullName}
                                </h4>
                                <div className="flex items-center space-x-4 mt-1">
                                  <div className="flex items-center space-x-2">
                                    <Mail className="w-3 h-3 text-gray-400" />
                                    <p className="text-sm text-gray-600">{parent.email}</p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Phone className="w-3 h-3 text-gray-400" />
                                    <p className="text-sm text-gray-600">
                                      {parent.phoneCode} {parent.phoneNumber}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRelationTypeColor(
                                  parent.relationType as unknown as number
                                )}`}
                              >
                                {getRelationTypeName(parent.relationType as unknown as number)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Bu öğrenciye atanmış veli bulunmuyor</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Öğrenci bulunamadı</h3>
              <p className="text-sm text-gray-500">Öğrenci detayları yüklenirken bir hata oluştu.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDetailModal;
